import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";

import logo from "../images/logo.png";
import { MarkeerooBrand } from "./MarkeerooBrand";

export interface Breadcrumb {
  label: string;
  to?: string; // undefined = current page (non-clickable)
  loading?: boolean;
}

interface AppHeaderProps {
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
}

export function AppHeader({ breadcrumbs = [], actions }: AppHeaderProps) {
  // Add "Markeroo" as first breadcrumb if not already present
  const allBreadcrumbs = breadcrumbs[0]?.label === "Markeroo"
    ? breadcrumbs
    : [{ label: "Markeroo", to: "/" }, ...breadcrumbs];

  // Find parent breadcrumb for mobile back button (second to last item with a link)
  const parentBreadcrumb = allBreadcrumbs
    .slice(0, -1)
    .reverse()
    .find((crumb) => crumb.to);

  return (
    <header className="sticky top-0 z-30 w-full border-b border-theme-primary bg-white shadow-sm">
      <div className="mx-auto flex w-full max-w-page items-center justify-between gap-2 sm:gap-4 px-4 sm:px-6 py-3 lg:px-8">
        {/* Logo + Breadcrumbs Section */}
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {/* Logo - always visible, always clickable */}
          <Link
            to="/"
            className="flex-shrink-0 transition hover:opacity-80"
            aria-label="Go to home"
          >
            <img src={logo} alt="Markeroo" className="h-8 w-8" />
          </Link>

          {/* Desktop: Full breadcrumb trail */}
          <nav
            aria-label="Breadcrumb"
            className="hidden min-w-0 items-center gap-2 sm:flex"
          >
            {allBreadcrumbs.map((crumb, index) => {
              const isLast = index === allBreadcrumbs.length - 1;
              const isFirst = index === 0;

              return (
                <Fragment key={`${crumb.label}-${index}`}>
                  {/* Separator (skip before first item) */}
                  {!isFirst && (
                    // eslint-disable-next-line no-restricted-syntax
                    <span className="flex-shrink-0 text-theme-muted" aria-hidden="true">
                      /
                    </span>
                  )}

                  {/* Breadcrumb item */}
                  {isLast ? (
                    // Current page - not clickable
                    <span
                      className="truncate max-w-[350px] font-semibold text-brand-950"
                      title={crumb.loading ? undefined : crumb.label}
                    >
                      {crumb.loading ? (
                        <span className="inline-block h-5 w-24 animate-pulse rounded bg-surface-secondary" />
                      ) : isFirst && crumb.label === "Markeroo" ? (
                        <MarkeerooBrand />
                      ) : (
                        crumb.label
                      )}
                    </span>
                  ) : crumb.to ? (
                    // Parent page - clickable link
                    <Link
                      to={crumb.to}
                      className="truncate max-w-[350px] font-medium text-brand-600 transition hover:text-brand-700"
                      title={crumb.loading ? undefined : crumb.label}
                    >
                      {crumb.loading ? (
                        <span className="inline-block h-5 w-24 animate-pulse rounded bg-surface-secondary" />
                      ) : isFirst && crumb.label === "Markeroo" ? (
                        <MarkeerooBrand className="text-brand-600 hover:text-brand-700" />
                      ) : (
                        crumb.label
                      )}
                    </Link>
                  ) : (
                    // Non-clickable breadcrumb
                    <span
                      className="truncate max-w-[350px] text-theme-secondary"
                      title={crumb.loading ? undefined : crumb.label}
                    >
                      {crumb.loading ? (
                        <span className="inline-block h-5 w-24 animate-pulse rounded bg-surface-secondary" />
                      ) : (
                        crumb.label
                      )}
                    </span>
                  )}
                </Fragment>
              );
            })}
          </nav>

          {/* Mobile: Back to parent link (if exists) */}
          {parentBreadcrumb && (
            <Link
              to={parentBreadcrumb.to!}
              className="flex items-center gap-1 font-medium text-brand-600 transition hover:text-brand-700 sm:hidden"
              aria-label={`Back to ${parentBreadcrumb.label}`}
            >
              {/* eslint-disable-next-line no-restricted-syntax */}
              <span aria-hidden="true">←</span>
              <span>{parentBreadcrumb.label}</span>
            </Link>
          )}
        </div>

        {/* Actions Section (AccountMenu, etc.) */}
        {actions && (
          <div className="flex flex-shrink-0 items-center gap-3">{actions}</div>
        )}
      </div>
    </header>
  );
}
